import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import {
  IconBrain, IconTrendingUp, IconAlertTriangle, IconHeadset,
  IconBulb, IconX,
} from "@tabler/icons-react";

/**
 * Modal explicativo dos relatórios avançados.
 * Linguagem propositalmente simples — explica como se fosse pra alguém
 * que nunca usou um dashboard. Cada métrica tem:
 *   - O QUE É: definição clara
 *   - COMO LER: o que o número significa pro seu negócio
 *   - EXEMPLO: caso prático
 */

const SECTIONS = [
  {
    id: "intro",
    title: "Como usar este painel",
    icon: IconBulb,
    color: "#f59e0b",
    intro:
      "Este painel mostra como o seu negócio anda. Cada caixinha (KPI) mostra um número importante. " +
      "As setas ↑ e ↓ mostram se aquilo melhorou ou piorou em comparação ao período anterior.",
    items: [
      {
        title: "🟢 Melhorou ↑ ou ↓",
        body:
          "O número mudou pra um lado bom. Exemplo: vendas subiram, OU cancelamentos caíram. " +
          "Em ambos os casos é bom!",
      },
      {
        title: "🔴 Piorou ↑ ou ↓",
        body: "O número mudou pra um lado ruim. Vale investigar o que aconteceu.",
      },
      {
        title: "⚪ Estável →",
        body: "Pouca ou nenhuma diferença em relação ao período anterior.",
      },
      {
        title: "— (traço)",
        body:
          "Não tem dado suficiente pra calcular ou você desligou a comparação. " +
          "Não é um erro — é só falta de informação naquele momento.",
      },
    ],
  },
  {
    id: "ai",
    title: "Meu agente IA está dando retorno?",
    icon: IconBrain,
    color: "#16a34a",
    intro:
      "Mede o quanto a sua Inteligência Artificial está trabalhando pra você. Se o agente IA está " +
      "respondendo, agendando e gerando dinheiro, esses números vão mostrar.",
    items: [
      {
        title: "% Atendimento Automatizado",
        body:
          "De cada 100 mensagens enviadas pro cliente, quantas a IA mandou sozinha (sem você precisar). " +
          "Quanto maior, mais a IA está te poupando tempo. Se você nunca atendeu manualmente no período, " +
          "esse número não aparece (porque não dá pra comparar).",
      },
      {
        title: "Mensagens IA enviadas",
        body:
          "Total de mensagens que a sua IA enviou pra clientes no período. Quanto mais, mais a IA está ativa.",
      },
      {
        title: "Mensagens Recebidas pela IA",
        body:
          "Mensagens que clientes enviaram e a IA leu/processou. Mostra o volume de conversas que entraram.",
      },
      {
        title: "Mensagens do Operador",
        body:
          "Mensagens que humanos da sua equipe enviaram pelo Chat (Central de Atendimento). Quanto menor, " +
          "mais a IA está dando conta.",
      },
      {
        title: "Agendamentos via IA",
        body:
          "Quantos agendamentos foram criados pela IA conversando com o cliente, sem você precisar marcar manualmente.",
      },
      {
        title: "% Agendamentos via IA",
        body:
          "Do total de agendamentos do período, quantos foram da IA. Se for 70%, sua IA está marcando 7 de cada 10.",
      },
      {
        title: "Receita gerada pela IA",
        body:
          "Soma do valor (R$) de todos os agendamentos REALIZADOS criados pela IA. " +
          "Considera apenas agendamentos passados que não foram cancelados, faltaram ou remarcados.",
      },
      {
        title: "Clientes vindos do WhatsApp",
        body:
          "Quantos contatos que entraram via WhatsApp viraram cliente no período. " +
          "Mostra o quanto a IA está convertendo conversa em cliente real.",
      },
    ],
  },
  {
    id: "growth",
    title: "Meu negócio está crescendo?",
    icon: IconTrendingUp,
    color: "#137ff7",
    intro:
      "Mostra se o seu negócio está atraindo gente nova, fechando vendas e ganhando dinheiro. " +
      "É a foto de saúde geral.",
    items: [
      {
        title: "Receita Realizada",
        body:
          "Total de dinheiro que entrou no período. É a SOMA de duas coisas: 💰 valor dos agendamentos " +
          "que aconteceram + 🎯 valor dos deals (negócios) ganhos no CRM.",
      },
      {
        title: "Receita de Agendamentos",
        body:
          "Só a parte da receita que veio de agendamentos. Conta os agendamentos passados " +
          "que não foram cancelados.",
      },
      {
        title: "Receita de Deals Ganhos (CRM)",
        body:
          "Só a parte da receita que veio de negócios fechados no Pipeline (CRM). " +
          "Contabiliza quando você move um contato pra coluna 'Ganho'.",
      },
      {
        title: "Ticket Médio",
        body:
          "Receita Realizada dividida pelo número de vendas. Quanto cada cliente paga em média. " +
          "Subir = bom, está vendendo coisas mais caras.",
      },
      {
        title: "Conversas Únicas",
        body:
          "Toda pessoa que trocou mensagem com você no período — cliente novo ou antigo que voltou. " +
          "Cada pessoa conta uma vez, não importa quantas mensagens enviou.",
      },
      {
        title: "Novos Contatos",
        body:
          "Pessoas que entraram na sua base pela primeira vez no período. " +
          "Novos Contatos sempre estão dentro das Conversas Únicas, mas nem toda Conversa Única é de alguém novo. " +
          "Exemplo: 80 Conversas Únicas com 30 Novos Contatos = 50 eram clientes antigos que voltaram.",
      },
      {
        title: "Novos Clientes (Conversões)",
        body:
          "Quantos contatos viraram cliente no período (clicando 'Transformar em Cliente' OU " +
          "movendo pro 'Ganho' do CRM, automaticamente).",
      },
      {
        title: "Taxa de Conversão",
        body:
          "Dos contatos novos do período, quantos viraram cliente (em qualquer momento). " +
          "Exemplo: 100 novos contatos, 20 viraram cliente = 20%. Quanto maior, melhor sua conversão.",
      },
      {
        title: "Total de Agendamentos",
        body: "Todos os agendamentos criados no período (independente de status).",
      },
      {
        title: "Confirmados",
        body: "Agendamentos onde o cliente respondeu confirmando que vai aparecer.",
      },
    ],
  },
  {
    id: "bottlenecks",
    title: "Onde estou perdendo dinheiro?",
    icon: IconAlertTriangle,
    color: "#ef4444",
    intro:
      "Os 'furos' do seu negócio. Aqui você vê onde você está perdendo dinheiro ou clientes — " +
      "pra agir e tampar o furo.",
    items: [
      {
        title: "Taxa de Cancelamento",
        body:
          "% dos agendamentos que foram cancelados. Se subir, algo está errado: política de cancelamento, " +
          "agente confirmando demais antes da hora, etc.",
      },
      {
        title: "Receita Perdida em Cancelamentos",
        body: "Quanto dinheiro você deixou de ganhar por causa de cancelamentos no período.",
      },
      {
        title: "Taxa de No-Show",
        body:
          "% dos agendamentos onde o cliente FALTOU sem cancelar. Esse é pior que cancelamento " +
          "porque você reservou o horário e não recebeu nada. Lembrete automático ajuda a baixar.",
      },
      {
        title: "Receita Perdida em No-Shows",
        body: "Quanto dinheiro foi reservado e nunca virou receita por causa de quem faltou.",
      },
      {
        title: "Confirmações Pendentes",
        body:
          "Agendamentos esperando o cliente confirmar. Se está acumulando, talvez sua IA não esteja " +
          "enviando lembrete OU o cliente não está respondendo.",
      },
      {
        title: "Sessões Abandonadas",
        body:
          "Atendimentos onde o cliente desistiu de esperar resposta. Isso é dinheiro indo embora — " +
          "alguém entrou em contato e ficou sem ser atendido.",
      },
      {
        title: "Taxa de Transferência",
        body:
          "% das sessões de atendimento onde a IA precisou passar o caso pra um humano. " +
          "Subir muito = IA está com dificuldade, vale revisar prompt/treinamento.",
      },
      {
        title: "Deals Perdidos",
        body:
          "Negócios marcados como 'Perdido' no CRM no período. Combinado com 'Motivos de Perda', " +
          "você descobre PORQUÊ está perdendo.",
      },
      {
        title: "Deals Estagnados",
        body:
          "Negócios que estão há MAIS DE 30 DIAS na mesma coluna do CRM, sem evolução. " +
          "Vale uma ligação ou desistir desse lead.",
      },
    ],
  },
  {
    id: "team",
    title: "Meu time está dando conta?",
    icon: IconHeadset,
    color: "#a855f7",
    intro:
      "Quando você tem operadores humanos atendendo, esse bloco mostra se eles estão dando conta " +
      "do volume e quem está produzindo mais.",
    items: [
      {
        title: "Sessões de Atendimento",
        body: "Total de atendimentos humanos que aconteceram no período (cada conversa = 1 sessão).",
      },
      {
        title: "Sessões Finalizadas",
        body: "Atendimentos que terminaram bem (cliente foi atendido até o fim).",
      },
      {
        title: "Sessões Abandonadas",
        body:
          "Atendimentos onde o cliente desistiu de esperar. Subir aqui = problema de capacidade ou " +
          "demora de resposta.",
      },
      {
        title: "Sessões Transferidas",
        body: "Atendimentos passados pra outro atendente ou departamento.",
      },
      {
        title: "Duração Média",
        body:
          "Tempo médio que cada sessão de atendimento durou. Sessões muito longas podem indicar " +
          "atendente ineficiente; muito curtas podem indicar atendimento mal-feito.",
      },
      {
        title: "Atividades CRM (Concluídas / Pendentes / Atrasadas)",
        body:
          "Tarefas que sua equipe criou no CRM: ligações, reuniões, follow-ups. " +
          "'Atrasadas' deveriam ter sido feitas e ainda não foram — atenção!",
      },
      {
        title: "Ranking de Atendentes",
        body:
          "Tabela mostrando quem atendeu quanto, quantas finalizou, transferiu e a taxa de transferência. " +
          "Útil pra ver quem está produtivo e quem precisa de ajuda/treinamento.",
      },
    ],
  },
];

const InsightsHelpModal = ({ show, onHide }) => {
  const [activeSection, setActiveSection] = useState("intro");
  const section = SECTIONS.find((s) => s.id === activeSection) || SECTIONS[0];
  const Icon = section.icon;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header className="insights-help__header" closeButton={false}>
        <div className="insights-help__title-wrap">
          <h4 className="insights-help__title">Como interpretar os relatórios</h4>
          <p className="insights-help__subtitle">
            Explicação simples de cada número que aparece neste painel
          </p>
        </div>
        <button className="insights-help__close" onClick={onHide}>
          <IconX size={20} />
        </button>
      </Modal.Header>

      <Modal.Body className="insights-help__body">
        <aside className="insights-help__sidebar">
          {SECTIONS.map((s) => {
            const SI = s.icon;
            return (
              <button
                key={s.id}
                type="button"
                className={`insights-help__sidebar-item ${activeSection === s.id ? "active" : ""}`}
                onClick={() => setActiveSection(s.id)}
                style={activeSection === s.id ? { borderLeftColor: s.color } : {}}
              >
                <span
                  className="insights-help__sidebar-icon"
                  style={{ color: s.color }}
                >
                  <SI size={16} />
                </span>
                <span>{s.title}</span>
              </button>
            );
          })}
        </aside>

        <main className="insights-help__content">
          <div className="insights-help__section-head">
            <div
              className="insights-help__section-icon"
              style={{ background: section.color }}
            >
              <Icon size={22} />
            </div>
            <div>
              <h5 className="insights-help__section-title">{section.title}</h5>
              <p className="insights-help__section-intro">{section.intro}</p>
            </div>
          </div>

          <div className="insights-help__items">
            {section.items.map((item) => (
              <div key={item.title} className="insights-help__item">
                <h6 className="insights-help__item-title">{item.title}</h6>
                <p className="insights-help__item-body">{item.body}</p>
              </div>
            ))}
          </div>
        </main>
      </Modal.Body>
    </Modal>
  );
};

export default InsightsHelpModal;
